/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Module: App
 * File     : App.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Frontend module within the React application.
 *   Exports: App
 *   
 *
 * Dependencies:
 *   - ./components/layout/Ads
 *   - ./components/layout/Footer
 *   - ./components/layout/Header
 *   - ./components/layout/NavigationDrawer
 *   - ./components/layout/RatingToasts
 *   - ./pages/Admin
 *   - ./pages/ApiDocs
 *   - ./pages/ClubDetail
 *   - ./pages/Clubs
 *   - ./pages/CreatePoll
 *   - ./pages/History
 *   - ./pages/Home
 *   - ./pages/Login
 *   - ./pages/PlexLibrary
 *   - ./pages/PostViewRatings
 *   - ./pages/PreviouslyWatched
 *   - ./pages/Pricing
 *   - ./pages/PrivacyPolicy
 *   - ./pages/Register
 *   - ./pages/Results
 *   - ./pages/Sitemap
 *   - ./pages/TermsAndConditions
 *   - ./pages/Vote
 *   - ./styles/theme
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Import: import App from '@/App';
 * -----------------------------------------------------------------------------
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Container, Box, ThemeProvider } from '@mui/material';
import theme from './styles/theme';
import NavigationDrawer from './components/layout/NavigationDrawer';
import { AutoAds } from './components/layout/Ads';
import Header from './components/layout/Header';
import CreatePoll from './pages/CreatePoll';
import Home from './pages/Home';
import Vote from './pages/Vote';
import Results from './pages/Results';
import History from './pages/History';
import Admin from './pages/Admin';
import Login from './pages/Login';
import Register from './pages/Register';
import PreviouslyWatched from './pages/PreviouslyWatched';
import PlexLibrary from './pages/PlexLibrary';
import RatingToasts from './components/layout/RatingToasts';
import PostViewRatings from './pages/PostViewRatings';
import Clubs from './pages/Clubs';
import ClubDetail from './pages/ClubDetail';
import ClubStreamer from './pages/ClubStreamer';
import ApiDocs from './pages/ApiDocs';
import { useAuth } from './context/AuthContext';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Sitemap from './pages/Sitemap';
import Footer from './components/layout/Footer';
import Profile from './pages/Profile';
import Metrics from './pages/Metrics';
import CLZMigration from './pages/CLZMigration';
import PhysicalMedia from './pages/PhysicalMedia';
import { API_BASE_URL } from './services/api';

const App = () => {
  const { user, loading } = useAuth();
  const canSeeApiDocs = !!user && (user.role === 'owner' || user.role === 'admin');
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AutoAds />
      <Header />
      <Box sx={{ display: 'flex', pt: 8 }}>
        <NavigationDrawer />
        <Container sx={{ ml: { md: '240px' }, py: 4 }}>
          {(() => {
            try {
              const stored = localStorage.getItem('ref:first');
              if (!stored) {
                const params = new URLSearchParams(window.location.search);
                const payload = {
                  referrer: document.referrer || '',
                  utmSource: params.get('utm_source') || '',
                  utmMedium: params.get('utm_medium') || '',
                  utmCampaign: params.get('utm_campaign') || '',
                  utmContent: params.get('utm_content') || '',
                  utmTerm: params.get('utm_term') || ''
                };
                localStorage.setItem('ref:first', JSON.stringify(payload));
                // Persist a client sessionId for anonymous attribution chain
                let sid = localStorage.getItem('ref:sid');
                if (!sid) {
                  sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2,10)}`;
                  localStorage.setItem('ref:sid', sid);
                }
                // Best effort send; backend will ignore if unauthenticated where required
                try {
                  fetch(`${API_BASE_URL}/analytics/visit`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' }, body: JSON.stringify(payload) });
                } catch (_) {}
                try {
                  fetch(`${API_BASE_URL}/analytics/event`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' }, body: JSON.stringify({ sessionId: sid, landingUrl: window.location.href, ...payload }) });
                } catch (_) {}
              }
            } catch (_) {}
          })()}
          <RatingToasts />
          <Routes key={window.location.pathname}>
            <Route path="/" element={<Home />} />
            <Route path="/create" element={<CreatePoll />} />
            <Route path="/vote" element={<Vote />} />
            <Route path="/results/:pollId" element={<Results />} />
            <Route path="/history" element={<History />} />
            <Route path="/previously-watched" element={<PreviouslyWatched />} />
            <Route path="/post-view-ratings" element={<PostViewRatings />} />
            <Route path="/clubs" element={<Clubs />} />
            <Route path="/clubs/:id" element={<ClubDetail />} />
            <Route path="/clubs/:clubId/streamer" element={!loading && !user ? <Navigate to="/" replace /> : <ClubStreamer />} />
            <Route path="/plex" element={<PlexLibrary />} />
            {canSeeApiDocs && <Route path="/api-docs" element={<ApiDocs />} />}
            {user?.role === 'owner' && <Route path="/clz-migration" element={<CLZMigration />} />}
            <Route path="/physical-media" element={<PhysicalMedia />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/metrics" element={<Metrics />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Footer />
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;


